import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

function shuffle(arr) {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * A rotating status indicator with a pulsing icon and fading text that cycles
 * through caller-provided statements. Used in place of plain spinners during
 * long AI waits to make the wait feel intentional rather than stuck.
 *
 * Props:
 *   statements   string[]            Required. The phrases to cycle through.
 *   compact      boolean (default false)   Tighter padding for narrow surfaces.
 *   inline       boolean (default false)   Skip the bubble wrapper. Use when
 *                                          the indicator already lives inside
 *                                          another message bubble.
 *   intervalMs   number  (default 2500)    Time between statement swaps.
 */
export default function RotatingStatusIndicator({
  statements,
  compact = false,
  inline = false,
  intervalMs = 2500,
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const queue = useMemo(() => shuffle(statements || []), [statements]);
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (queue.length <= 1) return undefined;
    let fadeTimeout;
    const tick = setInterval(() => {
      setFade(false);
      fadeTimeout = setTimeout(() => {
        setIndex((i) => (i + 1) % queue.length);
        setFade(true);
      }, 250);
    }, intervalMs);
    return () => {
      clearInterval(tick);
      clearTimeout(fadeTimeout);
    };
  }, [queue.length, intervalMs]);

  if (queue.length === 0) return null;

  const message = queue[index];

  const content = (
    <div className="flex items-center gap-2">
      <span className="relative inline-flex items-center justify-center">
        <span className={`absolute inset-0 rounded-full ${isDark ? 'bg-blue-500/30' : 'bg-blue-400/30'} animate-ping`} />
        <Sparkles size={14} className="relative text-blue-500" />
      </span>
      <span
        className={`text-xs transition-opacity duration-200 ${fade ? 'opacity-100' : 'opacity-0'} ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
      >
        {message}
      </span>
    </div>
  );

  if (inline) return content;

  return (
    <div className="flex justify-start">
      <div className={`rounded-xl ${compact ? 'px-3 py-2' : 'px-4 py-3'} ${isDark ? 'bg-gray-800' : 'bg-gray-100'} max-w-md`}>
        {content}
      </div>
    </div>
  );
}
